import { ReactNode } from "react";
import styled from "@emotion/styled";
import { text, status, borderRadius, duration, spacing } from "@atlas-int/tokens";
import ControlRow from "@atlas-int/ui/ControlRow";

const TutorialRow = styled(ControlRow)`
  justify-self: flex-end;
  align-self: flex-end;
  margin-left: auto;
`;

const LightbulbIcon = styled.div`
  color: ${status.gold};
  width: ${spacing[12]};
  height: ${spacing[12]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg {
    width: ${spacing[10]};
    height: ${spacing[10]};
    fill: ${text.onDark};
  }
`;

const TutorialLabel = styled.button`
  background: ${status.gold};
  color: ${text.onDark};
  border: none;
  padding: ${spacing[4]} ${spacing[8]};
  border-radius: ${borderRadius.base};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[4]};
  font-family: inherit;
  font-size: inherit;
  white-space: nowrap;
  transition: background ${duration.base} ease;
  &:hover {
    background: ${status.goldHover};
    color: ${text.onDark};
  }
`;

const SettingsRow = styled(ControlRow)`
  position: relative;
`;

interface SpecializedVisualizationControlPanelProps {
  /** Exporter location dropdown element */
  exporterDropdown: ReactNode;
  /** Year inputs element */
  yearInputs: ReactNode;
  /** Show table toggle element (for feasibility) */
  showTableToggle?: ReactNode;
  /** Product class toggle group element */
  productClassToggle: ReactNode;
  /** Settings section element (button + dropdown) */
  settingsSection: ReactNode;
  /** Whether to show the tutorial button */
  showTutorial?: boolean;
  /** Tutorial button click handler */
  onTutorialClick?: () => void;
  /** Tutorial lightbulb icon element */
  tutorialIcon?: ReactNode;
}

export const SpecializedVisualizationControlPanel = ({
  exporterDropdown,
  yearInputs,
  showTableToggle,
  productClassToggle,
  settingsSection,
  showTutorial = false,
  onTutorialClick,
  tutorialIcon,
}: SpecializedVisualizationControlPanelProps) => {
  return (
    <>
      <ControlRow label="Exporter" inputWidth="160px" flex maxWidth="275px">
        {exporterDropdown}
      </ControlRow>
      {yearInputs}
      {showTableToggle && <ControlRow>{showTableToggle}</ControlRow>}
      {productClassToggle}
      <SettingsRow>{settingsSection}</SettingsRow>
      {showTutorial && (
        <TutorialRow>
          <TutorialLabel onClick={onTutorialClick}>
            <LightbulbIcon>{tutorialIcon}</LightbulbIcon>
            Learn How to Use
          </TutorialLabel>
        </TutorialRow>
      )}
    </>
  );
};
